import { Calendar, FileText, Sparkles, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { InvoiceStatusBadge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatEuro, formatDateShort, type Invoice } from '../types'

const CANNOT_PAY: Invoice['paymentMethod'][] = ['SDD']

type Props = {
  invoice: Invoice | null
  /**
   * Stato dell'alert `bolletta` dal contratto attivo, se presente.
   *  - 'emessa' → mostra chip "NUOVA" + bordo cyan (richiamo positivo)
   *  - 'da pagare' o altro → bordo warning (richiamo all'azione)
   *  - undefined → card normale
   */
  alertStato?: string | null
}

export function UltimaBollettaCard({ invoice, alertStato }: Props) {
  if (!invoice) {
    return (
      <Card className="flex h-full">
        <EmptyState
          className="m-2 flex-1"
          icon={<FileText className="h-5 w-5" />}
          title="Nessuna bolletta recente"
          description="Le tue bollette compariranno qui."
        />
      </Card>
    )
  }

  const canPay = invoice.status !== 'pagata' && !CANNOT_PAY.includes(invoice.paymentMethod)

  const alertNorm = (alertStato ?? '').toLowerCase()
  const isNuova = alertNorm === 'emessa'
  const isDaPagare = alertStato != null && alertNorm !== 'emessa' && canPay

  return (
    <Card
      className={cn(
        'flex h-full flex-col transition-colors',
        isNuova && 'border-secondary/60 shadow-sm shadow-secondary/10',
        isDaPagare && 'border-warning/60 shadow-sm shadow-warning/10',
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Ultima bolletta
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">{invoice.periodLabel}</CardTitle>
              {isNuova && (
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-secondary">
                  <Sparkles className="h-3 w-3" />
                  Nuova
                </span>
              )}
            </div>
          </div>
          <InvoiceStatusBadge status={invoice.status} />
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        <p className="text-4xl font-bold tabular-nums text-foreground">
          {formatEuro(invoice.amount)}
        </p>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <InfoRow icon={<Calendar className="h-4 w-4" />} label="Scadenza">
            {formatDateShort(invoice.dueDate)}
          </InfoRow>
          <InfoRow icon={<Zap className="h-4 w-4" />} label="Consumo">
            {invoice.consumptionKwh} kWh
          </InfoRow>
        </div>

        <div className="flex flex-col gap-2 pt-2 sm:flex-row">
          {canPay && (
            <Link
              to={`/pagamenti/${encodeURIComponent(invoice.id)}`}
              className="flex h-12 min-h-12 flex-1 items-center justify-center rounded-xl bg-secondary px-5 text-center text-sm font-bold uppercase leading-none tracking-wide text-secondary-foreground shadow-sm shadow-secondary/20 transition-colors hover:bg-secondary/90"
            >
              Paga {formatEuro(invoice.amount)}
            </Link>
          )}
          <Link
            to={`/bollette/${encodeURIComponent(invoice.id)}`}
            className="flex h-12 min-h-12 flex-1 items-center justify-center rounded-xl border-[1.5px] border-secondary bg-transparent px-5 text-center text-sm font-semibold leading-none text-secondary transition-colors hover:bg-secondary/10"
          >
            Vedi dettaglio
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-muted/40 p-3">
      <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="font-semibold text-foreground">{children}</span>
    </div>
  )
}
